import { MorphScheme } from "../helpers/detect-morph-scheme";

export const arabicToPhonemeMap: any = {
    'ا': 'aa',
    'ب': 'b',
    'ت': 't',
    'ث': 'th',
    'ج': 'j',
    'ح': 'h',
    'خ': 'kh',
    'د': 'd',
    'ذ': 'dh',
    'ر': 'r',
    'ز': 'z',
    'س': 's',
    'ش': 'sh',
    'ص': 's',
    'ض': 'd',
    'ط': 't',
    'ظ': 'dh',
    'ع': 'aa',
    'غ': 'gh',
    'ف': 'f',
    'ق': 'q',
    'ك': 'k',
    'ل': 'l',
    'م': 'm',
    'ن': 'n',
    'ه': 'h',
    'و': 'w',
    'ي': 'y',
    'َ': 'a',
    'ُ': 'u',
    'ِ': 'i',
    'ْ': '',
    'ّ': '',
    'أ': 'aa',
    'إ': 'aa',
    'ؤ': 'w',
    'ئ': 'y',
    'ء': 'aa',
    'آ': 'aa',
    'ى': 'aa',
    'لا': 'laa',
    'ة': 't',
    'default': 'default'
};

export const englishToPhonemeMap: any = {
    'a': 'aa',
    'b': 'b',
    'c': 'k', // Assuming "c" as "k" by default
    'd': 'd',
    'e': 'e',
    'f': 'f',
    'g': 'g',
    'h': 'h',
    'i': 'i',
    'j': 'j',
    'k': 'k',
    'l': 'l',
    'm': 'm',
    'n': 'n',
    'o': 'o',
    'p': 'p',
    'q': 'q',
    'r': 'r',
    's': 's',
    't': 't',
    'u': 'u',
    'v': 'v',
    'w': 'w',
    'x': 'ks',
    'y': 'y',
    'z': 'z',
    'ch': 'ch',
    'sh': 'sh',
    'th': 'th',
    'dh': 'dh',
    'ng': 'ng',
    'oo': 'u',
    'ee': 'e',
    'ph': 'f',
    'wh': 'w',
    'qu': 'kw',
    'default': 'default'
};

export const pauseCharacters = [
    ".", ",", "!", "?", "؛", "،", ":", ";", "(", ")", "[", "]", "{", "}", "-", "–", "—", "…", "*", "**", "default"
];

export function extractEnglishPhonemes(text: string) {
    const phonemes = [];
    let i = 0;
    while (i < text.length) {
        const char = text[i].toLowerCase();
        const nextChar = text[i + 1]?.toLowerCase() || '';
        const pair = char + nextChar;
        if (/\s/.test(char)) {
            i++;
            continue;
        }
        else if (!/[a-z]/i.test(char)) {
            phonemes.push(englishToPhonemeMap.default);
            i++;
        }
        else if (englishToPhonemeMap[pair]) {
            phonemes.push(englishToPhonemeMap[pair]);
            i += 2;
        }
        else if (englishToPhonemeMap[char]) {
            phonemes.push(englishToPhonemeMap[char]);
            i++;
        }
        else {
            phonemes.push(englishToPhonemeMap.default);
            i++;
        }
    }
    return phonemes;
}

export const phonemeToMorphTargetMap: Record<string, Record<string, number>> = {
    "aa": { LowerTeeth_Down: 0.6, AI: 0.6, MouthDown: 0.2, UpperTeeth_Down: 0.3 },
    // TONGUE GROUP (increment 1): mouth values are UNCHANGED from the original.
    // Only the tongue morphs are added/fixed with the names that actually exist on
    // the rigs (ToungJawUp/ToungTh/ToungUp/ToungJawOut/ToungCDG/Toung_L/ToungAI) —
    // the old Toung_Jaw_Up / Toung_AI / Toung_Up names matched no mesh, so the
    // tongue never moved. Alveolars raise the tip, dentals push it to the teeth,
    // velars raise the back, r curls it up/back, laterals (l) use Toung_L.
    "laa": { LowerTeeth_Down: 0.6, AI: 0.6, MouthDown: 0.2, ToungJawUp: 0.4, Toung_L: 0.4 },
    "gh": { LowerTeeth_Down: 0.6, MouthDown: 0.5, ToungJawOut: 0.6, ToungCDG: 0.5, ToungAI: 0.4 },
    "kh": { LowerTeeth_Down: 0.6, MouthDown: 0.5, ToungJawOut: 0.6, ToungCDG: 0.5, ToungAI: 0.4 },
    "o": { LowerTeeth_Down: 0.6, Kiss: 0.5, OU: 0.6, AI: 0.4, MouthDown: 0.2 },
    "u": { LowerTeeth_Down: 0.6, OU: 0.6, Kiss: 0.5, AI: 0.4 },
    "f": { LowerTeeth_Down: 0.6, UpperTeeth_Down: 0.4, FV: 0.6 },
    "m": { LowerTeeth_Down: 0.6, PBM: 0.6, L_CheekPuff: 0.15, R_CheekPuff: 0.15 },
    "b": { LowerTeeth_Down: 0.6, PBM: 0.6, L_CheekPuff: 0.15, R_CheekPuff: 0.15 },
    "th": { LowerTeeth_Down: 0.6, Th: 0.6, UpperTeeth_Down: 0.5, ToungTh: 0.6, ToungJawUp: 0.4 },
    "s": { LowerTeeth_Down: 0.6, UpperTeeth_Down: 0.4, AI: 0.5, ToungJawUp: 0.35 },
    "z": { LowerTeeth_Down: 0.6, UpperTeeth_Down: 0.4, AI: 0.5, ToungJawUp: 0.35 },
    "n": { LowerTeeth_Down: 0.6, AI: 0.6, ToungJawUp: 0.75 },
    "r": { LowerTeeth_Down: 0.35, AI: 0.5, MouthDown: 0.12, ToungUp: 0.9, ToungJawOut: 0.5 },
    "sh": { LowerTeeth_Down: 0.6, AI: 0.6, ToungJawUp: 0.4 },
    "dh": { LowerTeeth_Down: 0.6, CDG: 1, ToungTh: 0.5 },
    "t": { LowerTeeth_Down: 0.6, UpperTeeth_Down: 0.4, AI: 0.5, ToungJawUp: 0.7 },
    "d": { LowerTeeth_Down: 0.6, UpperTeeth_Down: 0.4, AI: 0.5, ToungJawUp: 0.7 },
    "y": { LowerTeeth_Down: 0.6, AI: 0.6, ToungJawUp: 0.3 },
    "w": { LowerTeeth_Down: 0.6, Kiss: 0.5, OU: 0.6, AI: 0.4 },
    "e": { LowerTeeth_Down: 0.6, AI: 0.6 },
    "k": { LowerTeeth_Down: 0.6, AI: 0.6, ToungCDG: 0.6, ToungJawOut: 0.3 },
    "g": { LowerTeeth_Down: 0.6, AI: 0.6, ToungCDG: 0.6, ToungJawOut: 0.3 },
    "ch": { LowerTeeth_Down: 0.6, AI: 0.6, ToungJawUp: 0.45 },
    "j": { LowerTeeth_Down: 0.6, AI: 0.6, ToungJawUp: 0.45 },
    "h": { LowerTeeth_Down: 0.65, MouthDown: 0.4 },
    "l": { LowerTeeth_Down: 0.6, AI: 0.6, ToungJawUp: 0.9, Toung_L: 0.6 },
    "q": { LowerTeeth_Down: 0.6, AI: 0.6, ToungCDG: 0.6, ToungJawOut: 0.4 },
    "default": {
        LowerTeeth_Down: 0,
        UpperTeeth_Down: 0,
        R_UpperLip_Up: 0,
        L_UpperLip_Up: 0,
        AI: 0,
        MouthDown: 0,
        Toung_Jaw_Up: 0,
        Toung_L_JawSide: 0,
        Toung_Jaw_Out: 0,
        Toung_Long: 0,
        Toung_AI: 0,
        Toung_E: 0,
        Kiss: 0,
        OU: 0,
        PBM: 0,
        FV: 0,
        LipNarrow: 0,
        LipPucker: 0,
        L_CheekPuff: 0,
        R_CheekPuff: 0
    }
};

export const phonemeToMorphTargetMapMultiMesh: Record<string, Record<string, number>> = {
    "aa": { Head_o: 0.6, Teeth_o: 0.55, Teeth_MouthOpen: 0.65 },
    "laa": { Head_LL: 0.5, Head_Ch: 0.3, Teeth_LL: 0.4, Teeth_MouthOpen: 0.45 },
    "gh": { Head_Ch: 0.4, Teeth_Ch: 0.4, Teeth_MouthOpen: 0.4 },
    "kh": { Head_Ch: 0.4, Teeth_Ch: 0.4, Teeth_MouthOpen: 0.4 },
    "o": { Head_o: 0.7, Teeth_o: 0.65, Teeth_MouthOpen: 0.6 },
    "u": { Head_U: 0.65, Teeth_U: 0.6, Teeth_MouthOpen: 0.35 },
    "f": { Head_U: 0.35, Head_mm: 0.2, Teeth_MouthOpen: 0.2 },
    "m": { Head_mm: 0.65, Teeth_mm: 0.5, Teeth_MouthOpen: 0.03 },
    "b": { Head_mm: 0.65, Teeth_mm: 0.5, Teeth_MouthOpen: 0.03 },
    "p": { Head_Puff: 0.55, Teeth_Puff: 0.5, Teeth_MouthOpen: 0.07 },
    "th": { Head_U: 0.4, Head_mm: 0.2, Teeth_U: 0.3, Teeth_MouthOpen: 0.3 },
    "s": { Head_LL: 0.4, Head_mm: 0.2, Teeth_MouthOpen: 0.22 },
    "z": { Head_LL: 0.4, Head_mm: 0.2, Teeth_MouthOpen: 0.22 },
    "n": { Head_mm: 0.35, Head_LL: 0.2, Teeth_MouthOpen: 0.18 },
    "r": { Head_o: 0.4, Teeth_o: 0.35, Teeth_MouthOpen: 0.35 },
    "sh": { Head_o: 0.5, Head_U: 0.2, Teeth_MouthOpen: 0.4 },
    "dh": { Head_o: 0.4, Teeth_o: 0.35, Teeth_MouthOpen: 0.35 },
    "t": { Head_Ch: 0.4, Teeth_Ch: 0.4, Teeth_MouthOpen: 0.35 },
    "d": { Head_Ch: 0.4, Teeth_Ch: 0.4, Teeth_MouthOpen: 0.35 },
    "y": { Head_LL: 0.4, Head_E: 0.25, Teeth_MouthOpen: 0.28 },
    "w": { Head_U: 0.5, Teeth_U: 0.4, Teeth_MouthOpen: 0.25 },
    "e": { Head_E: 0.6, Teeth_mm: 0.28, Teeth_MouthOpen: 0.5 },
    "k": { Head_Ch: 0.5, Teeth_Ch: 0.5, Teeth_MouthOpen: 0.45 },
    "g": { Head_Ch: 0.5, Teeth_Ch: 0.5, Teeth_MouthOpen: 0.45 },
    "ch": { Head_Ch: 0.6, Teeth_Ch: 0.55, Teeth_MouthOpen: 0.42 },
    "j": { Head_Ch: 0.6, Teeth_Ch: 0.55, Teeth_MouthOpen: 0.42 },
    "h": { Head_o: 0.28, Teeth_MouthOpen: 0.4 },
    "l": { Head_LL: 0.55, Teeth_LL: 0.35, Teeth_MouthOpen: 0.4 },
    "q": { Head_Ch: 0.4, Teeth_Ch: 0.4, Teeth_MouthOpen: 0.42 },
    "default": {
        Head_o: 0, Teeth_o: 0,
        Head_Ch: 0, Teeth_Ch: 0,
        Head_U: 0, Teeth_U: 0,
        Head_mm: 0, Teeth_mm: 0,
        Head_LL: 0, Teeth_LL: 0,
        Head_E: 0, Teeth_E: 0,
        Head_Puff: 0, Teeth_Puff: 0,
        Head_Thinking: 0,
        Teeth_MouthOpen: 0
    }
};

export const getPhonemeMap = (scheme: MorphScheme) =>
    scheme === "MULTI_MESH" ? phonemeToMorphTargetMapMultiMesh : phonemeToMorphTargetMap;


export function extractArabicPhonemes(text: string) {
    const phonemes = [];
    for (let i = 0; i < text.length; i++) {
        const char = text[i];
        if (/\s/.test(char)) {
            continue;
        }
        if (arabicToPhonemeMap[char]) {
            phonemes.push(arabicToPhonemeMap[char]);
        } else {
            phonemes.push(arabicToPhonemeMap.default);
        }
    }
    return phonemes;
}
